import { useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Activity as ActivityIcon } from "lucide-react";
import { ActivityTable } from "@/components/activity/activity-table";
import { ActivityFilters } from "@/components/activity/activity-filters";
import { ActivityStats } from "@/components/activity/activity-stats";
import { ActivityDetailModal } from "@/components/activity/activity-detail-modal";
import { useActivityData } from "@/hooks/use-activity-data";
import type { ActivityTransaction } from "@/lib/types/activity";

interface ActivitySearchParams {
  search?: string;
}

export const Route = createFileRoute("/_app/activity")({
  component: ActivityPage,
  validateSearch: (search: Record<string, unknown>): ActivitySearchParams => ({
    search: search.search as string | undefined,
  }),
});

function ActivityPage() {
  const { search: initialSearch } = useSearch({ from: "/_app/activity" });

  const {
    transactions,
    stats,
    pagination,
    isLoading,
    filters,
    setPage,
    setPageSize,
    setFilters,
    resetFilters,
  } = useActivityData({
    initialFilters: initialSearch ? { search: initialSearch } : undefined,
  });

  const [selectedTransaction, setSelectedTransaction] =
    useState<ActivityTransaction | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ActivityIcon className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold text-foreground">Activity</h1>
      </div>

      <ActivityStats stats={stats} />

      <ActivityFilters
        filters={filters}
        onFilterChange={setFilters}
        onReset={resetFilters}
      />

      <ActivityTable
        transactions={transactions}
        pagination={pagination}
        isLoading={isLoading}
        onView={(transaction) => setSelectedTransaction(transaction)}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ActivityDetailModal
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onOpenChange={(open) => {
          if (!open) setSelectedTransaction(null);
        }}
      />
    </div>
  );
}
